<template>
  <MobileModal/>
  <MobileHelpSlider/>
  <!-- Main Product Section -->
  <div class="tw-grid sm:tw-grid-cols-12 tw-grid-cols-1 lg:tw-flex-row gap-6">
    <loading
        v-model:active="designerStore.variantLoading"
        :can-cancel="false"
        :is-full-page="true"
    />

    <!-- Options / Customisation section -->
    <div class="tw-col-span-5">
      <h1
          class="mb-4"
          style="font-weight: bold; font-size: 1.8em"
      >
        {{ designerStore.product.object.name }}
      </h1>

      <div
          style="padding-bottom: 20px"
          v-html="designerStore.product.object.description"
      />

      <template
          v-if="designerStore.product.object.dimensional_uplifts && designerStore.product.object.dimensions_location == 1"
      >
        <DimensionsInput
            @layer-update="layerUpdate"
        />
      </template>

      <VariantSelector
          @variant-changed="variantUpdate"
      />

      <template
          v-if="designerStore.product.object.dimensional_uplifts && designerStore.product.object.dimensions_location == 2"
      >
        <DimensionsInput
            @layer-update="layerUpdate"
        />
      </template>

      <template
          v-for="layer in getLayersOrdered"
          :key="layer.id"
      >
        <LayerSelector
            v-if="!layer.empty"
            :layer="layer"
            @layer-update="layerUpdate"
        />
      </template>

      <template
          v-if="designerStore.product.object.dimensional_uplifts && designerStore.product.object.dimensions_location == 0"
      >
        <DimensionsInput
            @layer-update="layerUpdate"
        />
      </template>

      <!-- Quantity Selector -->
      <QtySelector
          v-if="!designerStore.viewing"
      />

      <!-- Product Footer Information -->
      <ProductFooter
          v-if="!designerStore.viewing"
          @submit-cart="submitCart"
      />

      <ProductSections :items="getSections"/>
    </div>

    <!-- Image / View section -->
    <div
        class="tw-col-span-5 md:tw-sticky tw-relative"
        style="align-self: flex-end;bottom: 1rem;"
    >
      <div
          class="tw-relative"
          style="padding-top: 50px;"
      >
        <ImageLayering/>
      </div>

      <div
          v-if="!designerStore.viewing"
          class="tw-mb-4"
      >
        <div
            v-if="coreStore.settings.is_g4t"
            class="tw-text-center"
        >
          <h6>Your Price</h6>
          <span class="tw-original-price tw-line-through"/>
          <span class="tw-text-lg">£<span id="pricing">{{ designerStore.pricing.price }}</span></span>
          <span class="tw-text-gray-500 tw-font-bold tw-px-1.5">Inc Vat</span>
          <br>
          <br>
          <span class="tw-hidden md:tw-block">
            <AddToCart/>
          </span>
        </div>
        <div
            v-else
            class="tw-text-center"
        >
          <h6>Your Price</h6>
          <span class="tw-original-price tw-line-through"/>
          <input
              v-if="designerStore.selectedOptions"
              v-model="designerStore.selectedOptions.manual_price"
              type="text"
              class="tw-w-full tw-border tw-rounded tw-p-2"
              style="max-width: 200px"
          >
          <br>
          <br>
          <span class="tw-hidden md:tw-block">
            <AddToCart/>
          </span>
        </div>


        <div class="tw-grid tw-grid-cols-12 tw-gap-4 tw-mb-4">
          <!-- Left spacer: 1/12 on mobile, 2/12 on medium+ -->
          <div class="tw-col-span-1 md:tw-col-span-2"/>
        </div>

        <div class="tw-mt-4 tw-text-center"/>
      </div>
    </div>

    <MobileToolbar/>
    <div
        class="tw-col-span-2 md:tw-sticky tw-relative"
        style="align-self: flex-end;bottom: 1rem;"
    >
      <transition name="fade">
        <HelpContainer
            v-if="designerStore.helpArticle && designerStore.itemType !== 'quote'"
        />
      </transition>
    </div>
  </div>
</template>

<script>
import {useCoreStore} from "@/stores/CoreStore";
import {useDesignerStore} from '@/stores/DesignerStore';
import VariantSelector from './List/VariantSelector.vue'
import QtySelector from './List/QtySelector.vue'
import ProductFooter from './List/ProductFooter.vue'
import LayerSelector from './List/LayerSelector.vue'
import ImageLayering from './List/ImageLayering.vue'
import HelpContainer from './List/HelpContainer.vue'
import DimensionsInput from './List/DimensionsInput.vue'
import Loading from 'vue-loading-overlay';
import MobileToolbar from "@/components/backend/pages/designer/components/List/MobileToolbar.vue";
import MobileModal from "@/components/backend/pages/designer/components/List/MobileModal.vue";
import MobileHelpSlider from "@/components/backend/pages/designer/components/List/MobileHelpSlider.vue";
import 'vue-loading-overlay/dist/css/index.css';
import ProductSections from '@/components/backend/pages/designer/components/List/ProductSections.vue'
import AddToCart from '@/components/backend/pages/designer/components/List/AddToCart.vue'

export default {
  name: "ListLayout",
  components: {
    VariantSelector,
    QtySelector,
    ProductFooter,
    LayerSelector,
    ImageLayering,
    HelpContainer,
    DimensionsInput,
    Loading,
    MobileToolbar,
    MobileModal,
    MobileHelpSlider,
    ProductSections,
    AddToCart
  },
  inject: ['coreService', 'designerService'],
  setup() {
    const designerStore = useDesignerStore();
    const coreStore = useCoreStore();
    return {designerStore, coreStore};
  },
  computed: {
    getLayersOrdered() {
      if (this.designerStore.product.layers) {
        return Object.values(this.designerStore.product.layers).sort((a, b) => b.weight - a.weight)
      }
      return []
    },
    getSections() {
      const sections = this.designerStore.product.object.sections; // Replace with store path in your app

      if (!sections || !Array.isArray(sections)) {
        return []; // Return an empty array if the data is not as expected
      }

      // Use map to create a new array with the added 'id' property
      return sections.map((section, index) => {
        return {
          ...section, // Spread the existing properties of the section
          id: index    // Add the new 'id' property with the index value
        };
      });
    }
  },
  methods: {
    variantUpdate() {
      this.designerService.loadVariant()
    },
    layerUpdate(layerId = null) {
      if (this.designerService.runUpdates) {
        this.designerService.runUpdates(layerId)
      }
    },
    generateShare() {
      if (this.designerService.generateShareLink) {
        this.designerService.generateShareLink()
      }
    },
    productChanged(productId) {
      this.designerStore.loading = true;
      this.designerService.getProduct(productId)
    },
    callbacKRequest(callbackData) {
      this.designerService.generateCallback(callbackData)
    }
  }
}
</script>


<style scoped>

.help-tip {
  position: absolute;
  text-align: center;
  background-color: #29b6f6;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  z-index: 1000;

  font-size: 12px;
  line-height: 26px;
  cursor: default;
  transition: all 0.5s cubic-bezier(0.55, 0, 0.1, 1);
}

.help-tip:hover {
  cursor: pointer;
  background-color: #ccc;
}

.help-tip:before {
  content: '?';
  font-weight: 700;
  color: #fff;
}

.help-tip p {
  visibility: hidden;
  opacity: 0;
  z-index: 1000;
  text-align: left;
  background-color: #039be5;
  padding: 20px;
  width: 300px;
  position: absolute;
  border-radius: 4px;
  right: -4px;
  color: #fff;
  font-size: 13px;
  line-height: normal;
  transform: scale(0.7);
  transform-origin: 100% 0%;
  transition: all 0.5s cubic-bezier(0.55, 0, 0.1, 1);
}

.help-tip:hover p {
  cursor: default;
  visibility: visible;
  opacity: 1;
  transform: scale(1.0);
}

.help-tip p:before {
  position: absolute;
  content: '';
  width: 0;
  height: 0;
  border: 6px solid transparent;
  border-bottom-color: #039be5;
  right: 10px;
  top: -12px;
}

.help-tip p:after {
  width: 100%;
  height: 40px;
  content: '';
  position: absolute;
  top: -5px;
  left: 0;
  z-index: 10000;
}

.help-tip a {
  color: #fff;
  font-weight: 700;
}

.help-tip a:hover, .help-tip a:focus {
  color: #fff;
  text-decoration: underline;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity .5s;
}

.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */
{
  opacity: 0;
}

/* Tailwind CSS is assumed to be included globally. */
.button {
  border: none;
  color: white;
  padding: 10px 16px;
  text-align: center;
  text-decoration: none;
  display: inline-block;
  font-size: 16px;
  border-radius: 100px;
}
</style>